import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import { sortRows } from "../util/helpers";
import {
  InputGroup,
  InputGroupText,
  Input,
  FormGroup,
  Form,
} from "reactstrap";
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";

const DeviceProfilesTable = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  /* ----------------------------- Columns ----------------------------- */

  const visibleColumns = [
    { key: "device_profile_name", label: "Name" },
    { key: "device_profile_enabled", label: "Enabled" },
    { key: "device_profile_description", label: "Description" },
  ];

  const booleanColumns = ["device_profile_enabled"];

  const formatBoolean = (val) => {
    return val === true || val === "t" || val === "true" ? "Yes" : "No";
  };

  const userData = React.useMemo(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  }, []);

  const canCreate = hasPermission(userData, PERMISSIONS.CREATE);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);

  const canPerformActions = canCreate || canEdit || canDelete;

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (booleanColumns.includes(col.key)) {
        return formatBoolean(cell);
      }

      if (index === 0) {
        return canEdit ? (
          <span
            className="tr-row-name"
            onClick={() =>
              navigate(
                `/admin/hucs/edit/deviceProfile/${row.device_profile_uuid}`
              )
            }
            style={{ cursor: "pointer" }}
          >
            {cell ?? "-"}
          </span>
        ) : (
          <span style={{ fontWeight: 500 }}>{cell ?? "-"}</span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) =>
        canDelete ? (
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {
              e.stopPropagation();
              toggleDeleteModal(row);
            }}
          >
            Delete
          </Button>
        ) : null,
    });
  }

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  /* ----------------------------- Fetch Data ----------------------------- */

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;
      const res = await fetch(
        `${serverHost}/get_rows?table=v_device_profiles&pageSize=50&page=1&domain_uuid=${site_uuid}`
      );
      if (!res.ok) throw new Error("Failed to load device profiles");
      const data = await res.json();
      setProfiles(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filteredProfiles = React.useMemo(() => {
    if (!search) return profiles;

    return profiles.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    );
  }, [profiles, search, visibleColumns]);

  useEffect(() => {
    fetchProfiles();
  }, []);

  /* ----------------------------- Delete Logic ----------------------------- */

  const toggleDeleteModal = (profile = null) => {
    setProfileToDelete(profile);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!profileToDelete) return;

    try {
      const res = await fetch(
        `${serverHost}/delete_device_profile/${profileToDelete.device_profile_uuid}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error("Delete failed");

      await res.json();

      toggleDeleteModal();
      fetchProfiles();
    } catch (err) {
      alert(`Error deleting profile: ${err.message}`);
    }
  };

  /* ----------------------------- Loading / Error ----------------------------- */

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading profiles...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  /* ----------------------------- Table UI ----------------------------- */

  return (
    <>
      <HusariaHeader name="Device Profiles" parentName="HUCS" padding="4.5rem" />

      <Container fluid className="mt--6">
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            {/* LEFT: Title + Search */}
            <div className="d-flex align-items-center">

              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>

                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search profiles"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: "220px" }}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            {/* RIGHT: Actions */}
            {hasPermission(userData, PERMISSIONS.CREATE) && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate("/admin/hucs/add/deviceProfile")}
              >
                + Add Profile
              </Button>
            )}
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div style={{ overflowX: "auto" }}>
              <ToolkitProvider
                keyField="device_profile_uuid"
                data={filteredProfiles}
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      pagination={pagination}
                      bordered={false}
                      hover
                      rowStyle={{ cursor: "pointer" }}
                      classes="align-items-center table-flush"
                      rowEvents={{
                        onClick: (_, row) => {
                          if (canEdit) {
                            navigate(
                              `/admin/hucs/edit/deviceProfile/${row.device_profile_uuid}`
                            );
                          }
                        },
                      }}
                      noDataIndication={() =>
                        loading ? (
                          <div className="text-center">Loading...</div>
                        ) : (
                          <div className="text-center">No profiles found.</div>
                        )
                      }
                    />
                  </div>
                )}
              </ToolkitProvider>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* ----------------------------- Delete Modal ----------------------------- */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <Modal.Title
            style={{ color: isDark ? "#e2e8f0" : "#212529" }}
          >
            Delete Profile
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this device profile? This action
          cannot be undone.
        </Modal.Body>

        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeviceProfilesTable;
